import type { ApiEnvId } from '@/deploy/services/api-pterodactyl-config.service';
import type { ApiInstance, ApiLinkState, ApiService } from '@/deploy/services/api-services.service';
import type { Project, ProjectGroup } from '@/deploy/types';
import { ApiError } from '@/deploy/services/api';
import { API_ENV_GROUPS, apiGroupLabel, canonicalApiEnv, isExternalApi } from './apiEnvs';

// ============================================================
// Instances et répartition des APIs, miroir de ApiInstances côté CICD.
//
// Une API a une instance "default" (Principale) et jusqu'à 9 autres, chacune avec son serveur
// Ptero par groupe d'envs. La répartition (api.routing) dit, par env canonique, quelle instance
// sert chaque groupe de jeu ; un groupe non réparti reçoit la Principale. Un groupe réglé sur
// « Aucune » (NONE_INSTANCE) ne reçoit aucune instance : ses serveurs n'ont pas l'API, et ses
// placeholders restent non remplacés. Ce n'est pas une instance (jamais dans apiInstances).
//
// Un CICD plus ancien ne renvoie ni `instances` ni `routing` : la Principale est alors fabriquée
// depuis les champs racine et la page reste celle d'avant (instancesSupported = false).
// Même chose pour un projet sans groupes de jeu (hors NationsGlory, project.gameGroupsEnabled
// absent ou faux) : la page lit ses API par withoutInstances.
// ============================================================

export const DEFAULT_INSTANCE = 'default';
export const DEFAULT_INSTANCE_LABEL = 'Principale';
/**
 * Valeur de répartition « Aucune », miroir de ApiInstances.NONE : hors du format des clés
 * d'instance (« _ » refusé), donc jamais confondue avec une instance. Distincte de l'absence.
 */
export const NONE_INSTANCE = '_none';
export const NONE_INSTANCE_LABEL = 'Aucune';
export const MAX_API_INSTANCES = 10;
export const INSTANCE_KEY_MAX = 24;
export const INSTANCE_LABEL_MAX = 32;
/** Même règle que le CICD pour le suffixe du nom de base copié. */
export const DB_SUFFIX_PATTERN = /^[A-Za-z0-9_-]{1,32}$/;

/** Le CICD gère les instances (champ `instances` présent). */
export function instancesSupported(api: Pick<ApiService, 'instances'>): boolean {
  return Array.isArray(api.instances);
}

/**
 * Groupes de jeu, instances et répartition ouverts au projet : seulement si le CICD le dit
 * (NationsGlory). Absent (CICD plus ancien) = fermé.
 */
export function gameGroupsEnabled(project: Pick<Project, 'gameGroupsEnabled'> | null | undefined): boolean {
  return project?.gameGroupsEnabled === true;
}

/**
 * API vue par un projet sans groupes de jeu (Paladium) : sans `instances` ni `routing`, comme
 * sur un CICD plus ancien, pour que la page reste exactement celle d'avant les instances.
 */
export function withoutInstances(api: ApiService): ApiService {
  if (api.instances === undefined && api.routing === undefined) return api;
  return { ...api, instances: undefined, routing: undefined };
}

/** Instances de l'API, Principale en tête ; fabriquée depuis la racine sur un CICD plus ancien. */
export function apiInstances(api: ApiService): ApiInstance[] {
  const list = api.instances ?? [];
  const principal = list.find(isDefaultInstance);
  if (!principal) {
    return [{
      key: DEFAULT_INSTANCE,
      label: DEFAULT_INSTANCE_LABEL,
      isDefault: true,
      deployedVersions: api.deployedVersions,
      lastDeployedAt: api.lastDeployedAt,
      endpoints: api.endpoints,
    }, ...list];
  }
  return [principal, ...list.filter((i) => i !== principal)];
}

export function isDefaultInstance(inst: Pick<ApiInstance, 'key'>): boolean {
  return inst.key === DEFAULT_INSTANCE;
}

export function findInstance(api: ApiService, key: string): ApiInstance | undefined {
  return apiInstances(api).find((i) => i.key === key);
}

/** Libellé affiché : "Principale", "Iris"... */
export function instanceLabel(inst: Pick<ApiInstance, 'key' | 'label'>): string {
  return inst.label || (inst.key === DEFAULT_INSTANCE ? DEFAULT_INSTANCE_LABEL : inst.key);
}

/** Nom Ptero et nom affiché : "ModerationApi" pour la Principale, "ModerationApi [Iris]" sinon. */
export function instanceDisplayName(api: Pick<ApiService, 'name'>, inst: Pick<ApiInstance, 'key' | 'label'>): string {
  return isDefaultInstance(inst) ? api.name : `${api.name} [${instanceLabel(inst)}]`;
}

/** Clé du contrôle serveur : apiId pour la Principale (compatible), "apiId:clé" sinon. */
export function serverKey(api: Pick<ApiService, 'id'>, inst: Pick<ApiInstance, 'key'> | string): string {
  const key = typeof inst === 'string' ? inst : inst.key;
  return key === DEFAULT_INSTANCE ? api.id : `${api.id}:${key}`;
}

/**
 * Champ `instances` d'un deploy ou d'une Release : absent pour la seule Principale, et toujours
 * absent sur un CICD plus ancien, pour que ces appels restent ceux d'avant.
 */
export function instancesParam(api: ApiService, keys: string[]): string[] | undefined {
  if (!instancesSupported(api)) return undefined;
  if (keys.length === 1 && keys[0] === DEFAULT_INSTANCE) return undefined;
  return keys;
}

/** Placeholder des configs de mods : même règle que recube-fetch-apis.sh (ModerationApi → MODERATIONAPI). */
export function placeholderSlug(name: string): string {
  return name.toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

/**
 * Qualificatif de groupe des placeholders d'un serveur qui sert plusieurs groupes (le proxy) :
 * clé en majuscules, « - » devenu « _ » (lobby → LOBBY), réciproque de recube-fetch-apis.sh, qui
 * lit l'endpoint public avec ?group=<clé> pour chaque qualificatif trouvé.
 */
export function placeholderQualifier(groupKey: string): string {
  return groupKey.toUpperCase().replace(/-/g, '_');
}

/** « [[MODERATIONAPI_HOST@IRIS]] et [[MODERATIONAPI_PORT@IRIS]] » : placeholders qualifiés de l'API, par groupe. */
export function qualifiedPlaceholders(api: Pick<ApiService, 'name'>, groupKeys: string[]): string {
  const slug = placeholderSlug(api.name);
  return groupKeys.map((k) => {
    const q = placeholderQualifier(k);
    return `[[${slug}_HOST@${q}]] et [[${slug}_PORT@${q}]]`;
  }).join(', ');
}

/** Aperçu de la clé déduite du libellé, miroir de ApiInstances.deriveKey. */
export function deriveInstanceKey(label: string, existing: string[]): string {
  let base = (label ?? '').normalize('NFD').replace(/\p{M}+/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  if (base.length > INSTANCE_KEY_MAX) base = base.slice(0, INSTANCE_KEY_MAX).replace(/-+$/, '');
  if (!base) base = 'instance';
  const taken = new Set([...existing, DEFAULT_INSTANCE]);
  if (!taken.has(base)) return base;
  for (let n = 2; ; n++) {
    const suffix = `-${n}`;
    const head = base.length + suffix.length > INSTANCE_KEY_MAX
      ? base.slice(0, INSTANCE_KEY_MAX - suffix.length).replace(/-+$/, '')
      : base;
    if (!taken.has(head + suffix)) return head + suffix;
  }
}

/**
 * Contrôle client du libellé (le CICD reste la garde) : null si valide. « Principale » et
 * « Aucune » réservés, casse ignorée : une instance « Aucune » serait indiscernable de la valeur
 * de répartition dans l'audit, les confirmations et les messages.
 */
export function instanceLabelError(api: ApiService, label: string, key?: string): string | null {
  const l = label.trim();
  if (!l) return 'Libellé requis';
  if (l.length > INSTANCE_LABEL_MAX) return `${INSTANCE_LABEL_MAX} caractères au plus`;
  if (key !== DEFAULT_INSTANCE && l.toLowerCase() === DEFAULT_INSTANCE_LABEL.toLowerCase()) {
    return `« ${DEFAULT_INSTANCE_LABEL} » est réservé à l'instance par défaut`;
  }
  if (l.toLowerCase() === NONE_INSTANCE_LABEL.toLowerCase()) {
    return `« ${NONE_INSTANCE_LABEL} » est réservé à la répartition (groupe sans instance)`;
  }
  const clash = apiInstances(api).find((i) => i.key !== key && instanceLabel(i).toLowerCase() === l.toLowerCase());
  return clash ? `Libellé déjà pris par une autre instance : ${instanceLabel(clash)}` : null;
}

// ============================================================
// État d'une instance sur un env
// ============================================================

/** État du lien sur l'env ; undefined = inconnu (CICD plus ancien, liste non live, Ptero illisible). */
export function linkOn(inst: ApiInstance, env: ApiEnvId): ApiLinkState | undefined {
  return inst.links?.[canonicalApiEnv(env)];
}

export function versionOn(inst: ApiInstance, env: ApiEnvId): string | undefined {
  return inst.deployedVersions?.[canonicalApiEnv(env)];
}

export function endpointOn(inst: ApiInstance, env: ApiEnvId): string | undefined {
  return inst.endpoints?.[canonicalApiEnv(env)];
}

/**
 * Serveur utilisable pour Deploy et Release : bound ou legacy. Lien inconnu : une version connue
 * suffit, comme avant les instances. ambiguous compte à part (servi en lecture, bloqué ailleurs).
 */
export function hasServer(inst: ApiInstance, env: ApiEnvId): boolean {
  const link = linkOn(inst, env);
  if (link === undefined) return !!versionOn(inst, env);
  return link === 'bound' || link === 'legacy';
}

/** Serveur pilotable par power, console et fichiers : bound, legacy, ou ambiguous (premier serveur). */
export function hasControllableServer(inst: ApiInstance, env: ApiEnvId): boolean {
  return hasServer(inst, env) || linkOn(inst, env) === 'ambiguous';
}

// ============================================================
// Répartition
// ============================================================

/** Répartition de l'env (beta = dev) : groupe → clé d'instance, vide si absente. */
export function routingOn(api: Pick<ApiService, 'routing'>, env: ApiEnvId): Record<string, string> {
  return api.routing?.[canonicalApiEnv(env)] ?? {};
}

/**
 * Instance qui sert le groupe sur l'env : routing[env][groupe], sinon la Principale. Peut valoir
 * NONE_INSTANCE (« Aucune ») : à traiter par l'appelant, ce n'est pas une instance.
 */
export function routedInstance(api: Pick<ApiService, 'routing'>, env: ApiEnvId, group: string): string {
  const v = routingOn(api, env)[group];
  return v && v.trim() ? v : DEFAULT_INSTANCE;
}

/** Valeur de répartition « Aucune ». */
export function isNoneInstance(key: string | undefined): boolean {
  return key === NONE_INSTANCE;
}

/** Libellé d'une cible de répartition : « Aucune », celui de l'instance, ou la clé si elle a disparu. */
export function routingTargetLabel(api: ApiService, key: string): string {
  if (isNoneInstance(key)) return NONE_INSTANCE_LABEL;
  const inst = findInstance(api, key);
  return inst ? instanceLabel(inst) : key;
}

/**
 * API répartie sur l'env : au moins un groupe du projet envoyé vers une autre instance que la
 * Principale. « Aucune » ne compte pas, comme côté CICD : elle ne change rien aux autres groupes.
 */
export function isSplit(api: Pick<ApiService, 'routing'>, env: ApiEnvId, groups: ProjectGroup[]): boolean {
  const m = routingOn(api, env);
  return groups.some((g) => !!m[g.key] && m[g.key] !== DEFAULT_INSTANCE && !isNoneInstance(m[g.key]));
}

/** Groupes réglés sur « Aucune » sur l'env, dans l'ordre du projet : leurs serveurs ne reçoivent pas l'API. */
export function groupsWithoutInstance(api: Pick<ApiService, 'routing'>, env: ApiEnvId, groups: ProjectGroup[]): ProjectGroup[] {
  return groups.filter((g) => isNoneInstance(routedInstance(api, env, g.key)));
}

/**
 * Groupes servis par l'instance sur l'env, dans l'ordre des groupes du projet. Lus dans le DTO
 * live s'il les porte, sinon calculés par la même règle (la Principale reçoit les non répartis ;
 * un groupe sur « Aucune » n'est servi par aucune instance).
 */
export function servedGroupsOf(api: ApiService, inst: ApiInstance, env: ApiEnvId, groups: ProjectGroup[]): ProjectGroup[] {
  if (groups.length === 0) return [];
  const fromDto = inst.servedGroups?.[canonicalApiEnv(env)];
  if (fromDto) return groups.filter((g) => fromDto.includes(g.key));
  return groups.filter((g) => routedInstance(api, env, g.key) === inst.key);
}

/**
 * Envs canoniques et groupes où la répartition cite l'instance (hors Principale implicite).
 * Avec NONE_INSTANCE : les groupes réglés sur « Aucune », qui ne citent aucune instance.
 */
export function instanceCitations(api: Pick<ApiService, 'routing'>, key: string): { env: ApiEnvId; group: string }[] {
  const out: { env: ApiEnvId; group: string }[] = [];
  for (const env of API_ENV_GROUPS) {
    for (const [group, target] of Object.entries(api.routing?.[env] ?? {})) {
      if (target === key) out.push({ env, group });
    }
  }
  return out;
}

/** « Iris en Prod et en Pré-prod, Nova en Prod » : texte des citations, par groupe. */
export function citationsText(cited: { env: ApiEnvId; group: string }[], groups: ProjectGroup[]): string {
  const byGroup = new Map<string, string[]>();
  for (const c of cited) {
    const label = groupLabel(groups, c.group);
    byGroup.set(label, [...(byGroup.get(label) ?? []), apiGroupLabel(c.env)]);
  }
  return [...byGroup.entries()].map(([g, envs]) => `${g} en ${envs.join(' et en ')}`).join(', ');
}

export function groupLabel(groups: ProjectGroup[], key: string): string {
  return groups.find((g) => g.key === key)?.label ?? key;
}

/**
 * Instances présentes sur l'env, Principale puis ordre de création : celles qui ont un lien
 * (serveur, ou lien cassé à signaler), et, sur une API répartie, celles que la répartition
 * désigne sans serveur (à signaler en warning). Lien inconnu : une version connue suffit.
 * La Principale sans serveur est gardée dès qu'une autre instance est présente : elle sert les
 * groupes non répartis et les serveurs sans groupe, qui ne reçoivent alors pas l'API.
 */
export function instancesOnEnv(api: ApiService, env: ApiEnvId, groups: ProjectGroup[]): ApiInstance[] {
  const split = isSplit(api, env, groups);
  const all = apiInstances(api);
  const present = all.filter((inst) => {
    const link = linkOn(inst, env);
    if (link === undefined) return !!versionOn(inst, env);
    if (link !== 'none') return true;
    return split && servedGroupsOf(api, inst, env, groups).length > 0;
  });
  const principal = all[0];
  if (present.length > 0 && !present.includes(principal) && linkOn(principal, env) === 'none') {
    return [principal, ...present];
  }
  return present;
}

/** Bouton « Répartition » : API de jeu d'un CICD à jour, projet avec groupes ou API déjà multi-instances. */
export function routingAvailable(api: ApiService, groups: ProjectGroup[]): boolean {
  return instancesSupported(api) && !isExternalApi(api)
    && (groups.length > 0 || apiInstances(api).length > 1);
}

// ============================================================
// Divers
// ============================================================

/**
 * Exécute `fn` sur chaque élément, `limit` appels en parallèle au plus (Release de la ligne :
 * deux instances à la fois). `fn` ne doit pas lever : chaque appel gère son propre résultat.
 */
export async function runPool<T>(items: T[], limit: number, fn: (item: T) => Promise<void>): Promise<void> {
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const item = items[next++];
      try { await fn(item); } catch { /* résultat géré par fn */ }
    }
  };
  await Promise.all(Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, worker));
}

/** Message d'erreur d'une route d'instance ou de répartition, avec le cas d'un CICD pas encore à jour. */
export function instanceErrorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    if ((e.status === 404 && !/introuvable/i.test(e.message)) || e.status === 405) {
      return "Le CICD n'est pas encore à jour (route absente)";
    }
    return e.message;
  }
  return e instanceof Error ? e.message : 'Erreur';
}
